import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useCallback } from "react";

export interface Attachment {
  name: string;
  path: string;
  size: number;
  type: string;
  url: string;
}

export interface Conversation {
  id: string;
  company_id: string;
  project_id: string | null;
  title: string | null;
  type: string;
  created_at: string;
  members: { user_id: string; first_name: string; last_name: string; profile_photo_url: string | null }[];
  last_message?: { body: string; created_at: string; author_id: string } | null;
  unread_count: number;
}

export interface TeamMessage {
  id: string;
  conversation_id: string;
  author_id: string;
  body: string | null;
  attachments: Attachment[];
  read_by: Record<string, string>;
  created_at: string;
  author?: { first_name: string; last_name: string; profile_photo_url: string | null };
}

export const useConversations = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["team_conversations", user?.id],
    queryFn: async (): Promise<Conversation[]> => {
      if (!user?.id) return [];

      const { data: memberRows, error: mErr } = await (supabase
        .from("team_conversation_members") as any)
        .select("conversation_id")
        .eq("user_id", user.id);
      if (mErr) throw mErr;

      const convIds = (memberRows ?? []).map((r: any) => r.conversation_id);
      if (convIds.length === 0) return [];

      const { data: convos, error: cErr } = await (supabase
        .from("team_conversations") as any)
        .select("*")
        .in("id", convIds)
        .order("created_at", { ascending: false });
      if (cErr) throw cErr;

      // Fetch members flat (no PostgREST join)
      const { data: allMembers, error: amErr } = await (supabase
        .from("team_conversation_members") as any)
        .select("conversation_id, user_id")
        .in("conversation_id", convIds);
      if (amErr) throw amErr;

      // Collect unique user_ids and fetch profiles separately
      const memberUserIds = [...new Set((allMembers ?? []).map((m: any) => m.user_id))] as string[];
      let profilesMap: Record<string, any> = {};
      if (memberUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from("user_profiles")
          .select("user_id, first_name, last_name, profile_photo_url")
          .in("user_id", memberUserIds);
        for (const p of (profiles ?? [])) {
          profilesMap[(p as any).user_id] = p;
        }
      }

      const { data: lastMessages, error: lmErr } = await (supabase
        .from("team_messages") as any)
        .select("conversation_id, body, created_at, author_id, read_by")
        .in("conversation_id", convIds)
        .order("created_at", { ascending: false });
      if (lmErr) throw lmErr;

      const lastMsgMap: Record<string, any> = {};
      const unreadMap: Record<string, number> = {};
      for (const msg of (lastMessages ?? [])) {
        if (!lastMsgMap[msg.conversation_id]) {
          lastMsgMap[msg.conversation_id] = msg;
        }
        const readBy = msg.read_by as Record<string, string> ?? {};
        if (msg.author_id !== user.id && !readBy[user.id]) {
          unreadMap[msg.conversation_id] = (unreadMap[msg.conversation_id] ?? 0) + 1;
        }
      }

      return (convos ?? []).map((c: any) => ({
        ...c,
        members: (allMembers ?? [])
          .filter((m: any) => m.conversation_id === c.id)
          .map((m: any) => {
            const profile = profilesMap[m.user_id];
            return {
              user_id: m.user_id,
              first_name: profile?.first_name ?? "",
              last_name: profile?.last_name ?? "",
              profile_photo_url: profile?.profile_photo_url ?? null,
            };
          }),
        last_message: lastMsgMap[c.id] ?? null,
        unread_count: unreadMap[c.id] ?? 0,
      }));
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
};

export const useConversationMessages = (conversationId: string | null) => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["team_messages", conversationId],
    queryFn: async (): Promise<TeamMessage[]> => {
      if (!conversationId) return [];

      // Fetch messages flat (no PostgREST join)
      const { data, error } = await (supabase
        .from("team_messages") as any)
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(200);
      if (error) throw error;

      // Fetch author profiles separately
      const authorIds = [...new Set((data ?? []).map((m: any) => m.author_id))] as string[];
      let authorsMap: Record<string, any> = {};
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from("user_profiles")
          .select("user_id, first_name, last_name, profile_photo_url")
          .in("user_id", authorIds);
        for (const p of (profiles ?? [])) {
          authorsMap[(p as any).user_id] = p;
        }
      }

      return (data ?? []).map((m: any) => {
        const author = authorsMap[m.author_id];
        return {
          ...m,
          read_by: m.read_by ?? {},
          attachments: m.attachments ?? [],
          author: author ? {
            first_name: author.first_name ?? "",
            last_name: author.last_name ?? "",
            profile_photo_url: author.profile_photo_url ?? null,
          } : null,
        };
      });
    },
    enabled: !!conversationId,
  });

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`msgs-${conversationId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "team_messages", filter: `conversation_id=eq.${conversationId}` },
        () => {
          qc.invalidateQueries({ queryKey: ["team_messages", conversationId] });
          qc.invalidateQueries({ queryKey: ["team_conversations"] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [conversationId, qc]);

  return query;
};

export const useSendMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, companyId, body, projectId, attachments }: {
      conversationId: string;
      companyId: string;
      body?: string | null;
      projectId?: string | null;
      attachments?: Attachment[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await (supabase.from("team_messages") as any).insert({
        conversation_id: conversationId,
        company_id: companyId,
        author_id: user.id,
        body: body || null,
        project_id: projectId ?? null,
        attachments: attachments && attachments.length > 0 ? attachments : [],
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["team_messages", vars.conversationId] });
      qc.invalidateQueries({ queryKey: ["team_conversations"] });
    },
  });
};

export const useUploadAttachment = () => {
  return useMutation({
    mutationFn: async ({ file, conversationId }: { file: File; conversationId: string }): Promise<Attachment> => {
      const fileExt = file.name.split(".").pop();
      const uniqueName = `${crypto.randomUUID()}.${fileExt}`;
      const path = `${conversationId}/${uniqueName}`;

      const { error } = await supabase.storage
        .from("chat-attachments")
        .upload(path, file, { upsert: false });
      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from("chat-attachments")
        .getPublicUrl(path);

      return {
        name: file.name,
        path,
        size: file.size,
        type: file.type,
        url: urlData.publicUrl,
      };
    },
  });
};

export const useMarkAsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageIds }: { messageIds: string[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || messageIds.length === 0) return;
      for (const msgId of messageIds) {
        const { data: msg } = await (supabase.from("team_messages") as any)
          .select("read_by")
          .eq("id", msgId)
          .single();
        if (msg) {
          const readBy = { ...(msg.read_by as Record<string, string> ?? {}), [user.id]: new Date().toISOString() };
          await (supabase.from("team_messages") as any)
            .update({ read_by: readBy })
            .eq("id", msgId);
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_messages"] });
      qc.invalidateQueries({ queryKey: ["team_conversations"] });
    },
  });
};

export const useCreateConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, title, type, memberIds, projectId }: {
      companyId: string; title?: string; type: string; memberIds: string[]; projectId?: string | null;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: conv, error: cErr } = await (supabase.from("team_conversations") as any)
        .insert({
          company_id: companyId,
          title: title ?? null,
          type,
          project_id: projectId ?? null,
          created_by: user.id,
        })
        .select()
        .single();
      if (cErr) throw cErr;

      const allMembers = [...new Set([user.id, ...memberIds])];
      const { error: mErr } = await (supabase.from("team_conversation_members") as any)
        .insert(allMembers.map(uid => ({
          conversation_id: conv.id,
          user_id: uid,
          company_id: companyId,
        })));
      if (mErr) throw mErr;

      return conv;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_conversations"] });
    },
  });
};
